import { useMemo } from 'react'
import type { OrpcClient } from '@/orpc/client'
import { usePatientAiReport, type AiReportOps } from './usePatientAiReport'
import { useClient } from './useClient'

// Stored clinical-summary overview + staleness state served by
// GET /v2/patient/{id}/clinical-summary-overview. `upToDate` is only present on responses
// that opted into the staleness check (checkUpToDate: true) — undefined means "not
// verified yet" and renders as the blue Verifying badge.
export type ClinicalSummaryOverviewData = Awaited<
  ReturnType<OrpcClient['patientClinicalSummaryOverview']>
>

// The hook's full surface — passed down from the ClinicalSummary container to the header
// status controls and the Overview panel, which share one instance.
export type UsePatientClinicalSummaryOverviewResult = ReturnType<
  typeof usePatientClinicalSummaryOverview
>

// The overview instantiation of the shared ai-report hook (usePatientAiReport carries
// the load/verify/auto-rebuild/poll behavior).
export function usePatientClinicalSummaryOverview(patientId: string | undefined) {
  const client = useClient()
  const ops = useMemo<AiReportOps<ClinicalSummaryOverviewData>>(
    () => ({
      read: (id, checkUpToDate) => client.patientClinicalSummaryOverview({ id, checkUpToDate }),
      // `force` only ever rides the wire as an explicit true (the historical call shape).
      refresh: (id, force) =>
        client.patientClinicalSummaryOverviewRefresh(force ? { id, force: true } : { id }),
      label: 'clinical summary overview',
    }),
    [client],
  )
  return usePatientAiReport(patientId, ops)
}
